import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Phone,
  MessageSquare,
  Mail,
  Trash2,
  Flame,
  XCircle,
  Sparkles,
  FileText,
  ArrowRight,
  ListChecks,
  Route,
  Bell,
  CalendarClock,
  Home,
  MapPin,
  Wallet,
  UserCircle2,
  Clock,
  MessageCircle,
  Save,
  Bot,
  Pause,
  Play,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import AppShell from "@/components/layout/AppShell";
import StarScore from "@/components/crm/StarScore";
import AutoScoreBadge from "@/components/crm/AutoScoreBadge";
import ScoreBadge from "@/components/crm/ScoreBadge";
import CoachPanel from "@/components/crm/CoachPanel";
import WhatsAppConversation from "@/components/crm/WhatsAppConversation";
import ProgrammeSelect from "@/components/crm/ProgrammeSelect";
import ProcedureChecklist from "@/components/crm/ProcedureChecklist";
import Programme20j from "@/components/crm/Programme20j";
import RappelsSection from "@/components/crm/RappelsSection";
import WhatsAppQuickAction from "@/components/crm/WhatsAppQuickAction";
import RdvSetterButton from "@/components/crm/RdvSetterButton";
import AiRdvProposalBanner from "@/components/crm/AiRdvProposalBanner";
import ConversationStatusBadge from "@/components/crm/ConversationStatusBadge";
import {
  STATUTS,
  PRIORITES,
  LOCALISATIONS,
  TYPE_APPARTS,
  HORIZONS,
  formatDateTime,
  getInitials,
  statutColor,
  statutLabel,
  BUCKET_META,
  bucketOf,
  isUrgent,
  isStale,
  programmeProgress,
} from "@/lib/crmHelpers";
import { leadsApi, programmesApi } from "@/lib/crmApi";

const ICONS = {
  appel: Phone,
  sms: MessageSquare,
  email: Mail,
  note: FileText,
  statut: ArrowRight,
  priorite: Flame,
  relance: CalendarClock,
  creation: Sparkles,
  procedure: ListChecks,
  programme: Route,
};

const cleanPhone = (p = "") => p.replace(/[\s().-]/g, "");
const waLink = (p, msg = "") =>
  `https://wa.me/${cleanPhone(p).replace(/^\+?/, "")}${msg ? `?text=${encodeURIComponent(msg)}` : ""}`;

// Reasons offered in the "Marquer perdu" dialog. Free-text "other" is
// always available so a commercial can add nuance without us forcing them
// to fit a predefined bucket.
const LOST_REASONS = [
  { value: "budget", label: "Budget insuffisant" },
  { value: "concurrent", label: "A choisi un concurrent" },
  { value: "pas_interesse", label: "Pas / plus intéressé" },
  { value: "mauvais_timing", label: "Mauvais timing" },
  { value: "injoignable", label: "Injoignable / fantôme" },
  { value: "doublon", label: "Doublon / erreur de saisie" },
  { value: "autre", label: "Autre raison" },
];

export default function LeadDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("infos");
  // "Marquer perdu" dialog state — keeps the modal logic local to this page
  // since it's only invoked from one button.
  const [lostDialogOpen, setLostDialogOpen] = useState(false);
  const [lostReason, setLostReason] = useState("");
  const [lostNote, setLostNote] = useState("");

  const load = useCallback(async () => {
    try {
      const l = await leadsApi.get(id);
      setLead(l);
      setForm({
        nom: l.nom,
        telephone: l.telephone,
        email: l.email || "",
        statut: l.statut,
        priorite: l.priorite,
        score: l.score || 0,
        localisation: l.localisation || "dakar",
        type_appart: l.type_appart || "autre",
        horizon: l.horizon || "inconnu",
        budget: l.budget || "",
        montant_prevu: l.montant_prevu || 0,
        probabilite: l.probabilite ?? "",
        programme_immobilier: l.programme_immobilier || "",
        commercial: l.commercial || "",
        source: l.source || "",
        prochaine_action: l.prochaine_action?.slice(0, 10) || "",
        notes: l.notes || "",
        ville: l.ville || "",
        pays: l.pays || "",
        statut_relance: l.statut_relance || "",
        premier_contact_whatsapp: l.premier_contact_whatsapp || "",
        date_reception: l.date_reception || (l.created_at || "").slice(0, 10),
      });
    } catch (e) {
      toast.error("Lead introuvable");
      navigate("/");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const history = useMemo(
    () => [...(lead?.historique || [])].sort((a, b) => b.date.localeCompare(a.date)),
    [lead]
  );
  const progress = useMemo(() => programmeProgress(lead?.programme), [lead]);

  if (loading || !lead || !form) {
    return (
      <AppShell topbarProps={{ showSearch: false, showAdd: false }}>
        <div className="flex min-h-[60vh] items-center justify-center text-muted-foreground">
          Chargement...
        </div>
      </AppShell>
    );
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Auto-save a single field on blur. Used by the inline identity inputs
  // (nom, telephone, email, ville, pays) so a commercial doesn't lose
  // edits when they navigate away without clicking "Enregistrer". We send
  // ONLY the changed field to keep the patch minimal and avoid clobbering
  // numeric fields like probabilite that have their own coercion logic
  // in `save()`.
  const saveField = async (key) => {
    if (!form || !lead) return;
    const current = (form?.[key] ?? "").toString().trim();
    const original = (lead?.[key] ?? "").toString().trim();
    if (current === original) return; // no-op, skip the round-trip
    try {
      const updated = await leadsApi.update(lead.id, { [key]: form[key] });
      setLead(updated);
      // No toast — silent success keeps the page calm. Failures still
      // surface via the catch.
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Modification non sauvegardée"
      );
      // Roll the local form value back so the UI stays consistent with
      // what's persisted server-side.
      setForm((f) => ({ ...f, [key]: lead[key] ?? "" }));
    }
  };
  const bMeta = BUCKET_META[bucketOf(lead)];
  const urgent = isUrgent(lead);
  const stale = isStale(lead);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, prochaine_action: form.prochaine_action || null };
      payload.montant_prevu = payload.montant_prevu === "" || payload.montant_prevu == null
        ? 0
        : Number(payload.montant_prevu);
      payload.probabilite = payload.probabilite === "" || payload.probabilite == null
        ? null
        : Math.max(0, Math.min(100, Number(payload.probabilite)));
      if (payload.probabilite === null) delete payload.probabilite;
      const updated = await leadsApi.update(lead.id, payload);
      setLead(updated);
      toast.success("Lead mis à jour");
    } catch (e) {
      toast.error("Erreur de sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (newStatus) => {
    try {
      const updated = await leadsApi.update(lead.id, { statut: newStatus });
      setLead(updated);
      setForm((f) => ({ ...f, statut: newStatus }));
      toast.success(`Statut → ${statutLabel(newStatus)}`);
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const handleProcedureToggle = async (key, value) => {
    const updated = await leadsApi.updateProcedure(lead.id, key, value);
    setLead(updated);
  };
  const handleProgrammeToggle = async (jour, idx, fait) => {
    const updated = await leadsApi.updateProgramme(lead.id, jour, idx, fait);
    setLead(updated);
    toast.success(fait ? "Étape marquée faite" : "Étape réouverte");
  };
  const handleDelete = async () => {
    if (!window.confirm(`Supprimer ${lead.nom} ?`)) return;
    await leadsApi.remove(lead.id);
    toast.success("Lead supprimé");
    navigate("/");
  };

  const handleCall = () => { window.location.href = `tel:${cleanPhone(lead.telephone)}`; };
  const handleSMS = () => { window.location.href = `sms:${cleanPhone(lead.telephone)}`; };
  const handleEmail = () => {
    if (!lead.email) return toast.error("Aucun email renseigné");
    window.location.href = `mailto:${lead.email}`;
  };
  const handleWhatsApp = () => {
    window.open(waLink(lead.telephone, `Bonjour ${lead.nom},`), "_blank", "noopener");
  };
  const markHot = async () => {
    const updated = await leadsApi.update(lead.id, { priorite: "haute", score: 4 });
    setLead(updated);
    toast.success("Marqué HOT");
  };
  const toggleAiAgent = async () => {
    const nextPaused = !lead.ai_paused;
    try {
      await leadsApi.setAiAgent(lead.id, nextPaused);
      const fresh = await leadsApi.get(lead.id);
      setLead(fresh);
      toast.success(nextPaused ? "Agent IA mis en pause" : "Agent IA réactivé");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };
  // "Perdu" was a single-click destructive action — easy to fire by
  // accident and losing the "why" altogether. We now open a small modal
  // that asks for a reason (drop-down + optional free text) and only
  // changes the status once confirmed. The reason is logged in the lead's
  // historique so the team can review wins/losses later.
  const openLostDialog = () => {
    setLostReason("");
    setLostNote("");
    setLostDialogOpen(true);
  };
  const confirmLost = async () => {
    if (!lostReason) {
      toast.error("Choisis une raison");
      return;
    }
    try {
      const reasonLabel =
        LOST_REASONS.find((r) => r.value === lostReason)?.label || lostReason;
      const description = lostNote.trim()
        ? `Lead marqué perdu — ${reasonLabel} : ${lostNote.trim()}`
        : `Lead marqué perdu — ${reasonLabel}`;
      // Log first so the historique entry survives even if the update
      // fails mid-way (e.g. network blip on the status patch below).
      await leadsApi.addAction(lead.id, { type: "note", description });
      await changeStatus("perdu");
      setLostDialogOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <AppShell
      topbarProps={{
        showSearch: false,
        showAdd: false,
      }}
    >
      <div className="mx-auto max-w-[1400px] px-4 pb-24 pt-4 sm:px-6 sm:pb-8 sm:pt-6 lg:px-8 lg:pt-8">
        {/* Back + breadcrumb */}
        <div className="mb-4 flex items-center justify-between gap-3">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            data-testid="back-btn"
            className="gap-2 -ml-2"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Retour</span>
          </Button>
          <div className="flex items-center gap-2">
            <Select value={lead.statut} onValueChange={changeStatus}>
              <SelectTrigger
                className="w-[150px] sm:w-[180px]"
                data-testid="status-quick-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUTS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Hero header */}
        <div className="relative overflow-hidden rounded-2xl border border-border/60 bg-card p-5 shadow-soft sm:p-6">
          <div className="absolute inset-0 gradient-mesh opacity-40" aria-hidden />
          <div className="relative flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-start gap-4">
              <div className="relative flex h-16 w-16 flex-none items-center justify-center rounded-full bg-gradient-to-br from-accent-navy to-accent-blue font-heading text-xl font-bold text-white shadow-elevated sm:h-20 sm:w-20 sm:text-2xl">
                {getInitials(lead.nom)}
                {urgent && (
                  <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] text-white shadow-soft animate-pulse-soft">
                    <Flame className="h-3 w-3" />
                  </span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="font-heading truncate text-xl font-bold tracking-tight sm:text-2xl">
                    {lead.nom}
                  </h1>
                  <span
                    className={`rounded-md border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${bMeta.color}`}
                    title={bMeta.full}
                  >
                    {bMeta.label}
                  </span>
                  {/* Hide the statut badge when the bucket already conveys
                      the same information — "PERDU PERDU" / "GAGNÉ GAGNÉ"
                      were showing side by side because both lookups landed
                      on the same label. We keep both visible for in-progress
                      buckets (hot/warm/nurture/pending) since the statut
                      adds nuance there (Nouveau vs Contacté vs RDV…). */}
                  {!(["lost", "won"].includes(bucketOf(lead))) && (
                    <span
                      className={`rounded-md px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider ${statutColor(lead.statut)}`}
                      title="Statut prospect"
                    >
                      {statutLabel(lead.statut)}
                    </span>
                  )}
                  {stale && (
                    <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-500/90 px-2 py-0.5 text-[10px] font-bold uppercase text-white">
                      <Clock className="h-2.5 w-2.5" />
                      Sans contact &gt; 2j
                    </span>
                  )}
                  {/* Agent IA — état + bouton pause/réactivation. Cliquable pour
                      reprendre la main ou rendre la conversation à l'IA. */}
                  <button
                    type="button"
                    onClick={toggleAiAgent}
                    data-testid="ai-agent-toggle"
                    title={
                      lead.ai_paused
                        ? `Agent IA en pause${lead.ai_paused_reason ? ` — ${lead.ai_paused_reason}` : ""}. Cliquez pour réactiver.`
                        : "Agent IA actif. Cliquez pour mettre en pause (prise en main manuelle)."
                    }
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase transition ${
                      lead.ai_paused
                        ? "bg-slate-200 text-slate-600 hover:bg-slate-300"
                        : "bg-emerald-500/90 text-white hover:bg-emerald-600"
                    }`}
                  >
                    <Bot className="h-2.5 w-2.5" />
                    {lead.ai_paused ? "IA en pause" : "IA active"}
                    {lead.ai_paused ? (
                      <Play className="h-2.5 w-2.5" />
                    ) : (
                      <Pause className="h-2.5 w-2.5" />
                    )}
                  </button>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <StarScore
                    value={form.score}
                    onChange={(v) => {
                      set("score", v);
                      leadsApi.update(lead.id, { score: v }).then(setLead);
                    }}
                    size="md"
                    testId="page-stars"
                  />
                  <ScoreBadge leadId={lead.id} testId="page-quality-badge" />
                  {/* v2.1.0 — auto-score pill with signal breakdown on hover */}
                  <AutoScoreBadge lead={lead} size="lg" />
                </div>

                {/* Quick info chips */}
                <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs">
                  <a
                    href={`tel:${cleanPhone(lead.telephone)}`}
                    className="inline-flex items-center gap-1.5 hover:text-foreground link-underline"
                  >
                    <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                    {lead.telephone}
                  </a>
                  {lead.email && (
                    <a
                      href={`mailto:${lead.email}`}
                      className="inline-flex items-center gap-1.5 truncate hover:text-foreground link-underline"
                    >
                      <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="truncate max-w-[200px]">{lead.email}</span>
                    </a>
                  )}
                  {lead.budget && (
                    <span className="inline-flex items-center gap-1.5">
                      <Wallet className="h-3.5 w-3.5 text-accent-gold" />
                      <span className="font-semibold text-accent-gold">{lead.budget}</span>
                    </span>
                  )}
                  {lead.programme_immobilier && (
                    <span className="inline-flex items-center gap-1.5">
                      <Home className="h-3.5 w-3.5 text-muted-foreground" />
                      {lead.programme_immobilier}
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                    {lead.localisation === "dakar" ? "Dakar" : "Diaspora"}
                    {lead.type_appart && lead.type_appart !== "autre" && ` · ${lead.type_appart.toUpperCase()}`}
                  </span>
                  {lead.commercial && (
                    <span className="inline-flex items-center gap-1.5">
                      <UserCircle2 className="h-3.5 w-3.5 text-muted-foreground" />
                      {lead.commercial}
                    </span>
                  )}
                  {lead.conversation_status && lead.conversation_status !== "nouveau" && (
                    <ConversationStatusBadge status={lead.conversation_status} rdvAt={lead.rdv_at} />
                  )}
                </div>
              </div>
            </div>

            {/* Programme progress */}
            <div className="rounded-xl border border-border/60 bg-background/60 p-3 sm:min-w-[200px]">
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-muted-foreground">Programme conversion</span>
                <span className="font-heading font-bold">{progress}%</span>
              </div>
              <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-accent-navy to-accent-gold transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="mt-2 text-[10px] text-muted-foreground">
                {progress >= 100 ? "Programme terminé" : "En cours"}
              </p>
            </div>
          </div>

          {/* Quick actions row (desktop) */}
          <div className="relative mt-5 hidden flex-wrap gap-2 sm:flex">
            <Button size="sm" variant="outline" onClick={handleCall} className="gap-1.5" data-testid="page-call-btn">
              <Phone className="h-3.5 w-3.5" />
              Appeler
            </Button>
            <WhatsAppQuickAction lead={lead} onSent={async () => {
              try { await leadsApi.addAction(lead.id, { type: "note", description: "WhatsApp envoyé via modèle" }); const fresh = await leadsApi.get(lead.id); setLead(fresh); } catch {}
            }} />
            <RdvSetterButton lead={lead} onUpdated={async () => {
              try { const fresh = await leadsApi.get(lead.id); setLead(fresh); } catch {}
            }} />
            <Button size="sm" variant="outline" onClick={handleSMS} className="gap-1.5" data-testid="page-sms-btn">
              <MessageSquare className="h-3.5 w-3.5" />
              SMS
            </Button>
            <Separator orientation="vertical" className="h-7 mx-1" />
            <Button size="sm" variant="outline" onClick={markHot} className="gap-1.5" data-testid="page-hot-btn">
              <Flame className="h-3.5 w-3.5 text-red-500" />
              Marquer HOT
            </Button>
            {/* v1.9.2 — "Logger une interaction" retiré : les actions
                Appeler/WhatsApp/SMS/Fixer RDV créent déjà automatiquement
                une entrée dans l'historique du lead, le bouton manuel
                doublonnait et créait de la confusion. */}
            <div className="ml-auto flex gap-1">
              <Button size="sm" variant="ghost" onClick={openLostDialog} data-testid="page-lost-btn" className="gap-1.5 text-muted-foreground hover:text-red-500">
                <XCircle className="h-3.5 w-3.5" />
                Perdu
              </Button>
              <Button size="sm" variant="ghost" onClick={handleDelete} data-testid="page-delete-btn" className="gap-1.5 text-muted-foreground hover:text-red-500">
                <Trash2 className="h-3.5 w-3.5" />
                Supprimer
              </Button>
            </div>
          </div>
        </div>

        {/* RDV proposé par l'IA — bandeau de validation */}
        <AiRdvProposalBanner
          lead={lead}
          onUpdated={async (fresh) => {
            if (fresh) {
              setLead(fresh);
            } else {
              try {
                const reloaded = await leadsApi.get(lead.id);
                setLead(reloaded);
              } catch {}
            }
          }}
        />

        {/* AI Coach Panel */}
        <div className="mt-6">
          <CoachPanel
            lead={lead}
            onAfterAction={async () => {
              try {
                const fresh = await leadsApi.get(lead.id);
                setLead(fresh);
              } catch {}
            }}
          />
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab} className="mt-6 w-full">
          <div className="scrollbar-thin -mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
            <TabsList className="inline-flex w-auto sm:grid sm:w-full sm:grid-cols-6">
              <TabsTrigger value="infos" data-testid="page-tab-infos">Infos</TabsTrigger>
              <TabsTrigger value="whatsapp" data-testid="page-tab-whatsapp" className="gap-1">
                <MessageCircle className="h-3.5 w-3.5 text-emerald-600" />
                WhatsApp
              </TabsTrigger>
              <TabsTrigger value="rappels" data-testid="page-tab-rappels" className="gap-1">
                <Bell className="h-3.5 w-3.5" />
                Rappels
              </TabsTrigger>
              <TabsTrigger value="procedure" data-testid="page-tab-procedure" className="gap-1">
                <ListChecks className="h-3.5 w-3.5" />
                Procédure
              </TabsTrigger>
              <TabsTrigger value="programme" data-testid="page-tab-programme" className="gap-1">
                <Route className="h-3.5 w-3.5" />
                Programme
              </TabsTrigger>
              <TabsTrigger value="historique" data-testid="page-tab-historique" className="gap-1">
                <Clock className="h-3.5 w-3.5" />
                Historique
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Infos */}
          <TabsContent value="infos" className="pt-5 animate-fade-in">
            <div className="grid gap-5 lg:grid-cols-3">
              <div className="rounded-xl border border-border/60 bg-card p-5 lg:col-span-2">
                <h3 className="font-heading mb-4 flex items-center gap-2 text-sm font-semibold">
                  <UserCircle2 className="h-4 w-4 text-muted-foreground" />
                  Identité
                </h3>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>Nom</Label>
                    {/* onBlur auto-saves so a commercial can edit any field
                        and tab away without losing the change. Same pattern
                        below for telephone, email, ville, pays. */}
                    <Input
                      value={form.nom}
                      onChange={(e) => set("nom", e.target.value)}
                      onBlur={() => saveField("nom")}
                      data-testid="edit-nom"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Téléphone</Label>
                    <Input
                      value={form.telephone}
                      onChange={(e) => set("telephone", e.target.value)}
                      onBlur={() => saveField("telephone")}
                      data-testid="edit-telephone"
                    />
                  </div>
                  <div className="space-y-1.5 md:col-span-2">
                    <Label>Email</Label>
                    <Input
                      value={form.email}
                      onChange={(e) => set("email", e.target.value)}
                      onBlur={() => saveField("email")}
                      data-testid="edit-email"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Ville</Label>
                    <Input
                      value={form.ville}
                      onChange={(e) => set("ville", e.target.value)}
                      onBlur={() => saveField("ville")}
                      placeholder="ex: Dakar"
                      data-testid="edit-ville"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Pays</Label>
                    <Input
                      value={form.pays}
                      onChange={(e) => set("pays", e.target.value)}
                      onBlur={() => saveField("pays")}
                      placeholder="ex: Sénégal"
                      data-testid="edit-pays"
                    />
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-border/60 bg-card p-5">
                <h3 className="font-heading mb-4 flex items-center gap-2 text-sm font-semibold">
                  <Sparkles className="h-4 w-4 text-muted-foreground" />
                  Pipeline
                </h3>
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label>Statut</Label>
                    <Select value={form.statut} onValueChange={(v) => set("statut", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUTS.map((s) => (<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Priorité</Label>
                    <Select value={form.priorite} onValueChange={(v) => set("priorite", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {PRIORITES.map((p) => (<SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Statut relance</Label>
                    <Input
                      value={form.statut_relance}
                      onChange={(e) => set("statut_relance", e.target.value)}
                      placeholder="à contacter / relancé"
                      data-testid="edit-statut-relance"
                    />
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-border/60 bg-card p-5 lg:col-span-2">
                <h3 className="font-heading mb-4 flex items-center gap-2 text-sm font-semibold">
                  <Home className="h-4 w-4 text-muted-foreground" />
                  Projet immobilier
                </h3>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>Localisation</Label>
                    <Select value={form.localisation} onValueChange={(v) => set("localisation", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {LOCALISATIONS.map((l) => (<SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Type de bien</Label>
                    <Select value={form.type_appart} onValueChange={(v) => set("type_appart", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {TYPE_APPARTS.map((t) => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Horizon d'achat</Label>
                    <Select value={form.horizon} onValueChange={(v) => set("horizon", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {HORIZONS.map((h) => (<SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Programme préféré</Label>
                    <ProgrammeSelect
                      value={form.programme_immobilier}
                      onChange={(v) => set("programme_immobilier", v)}
                      testId="edit-programme-immo"
                      showInactive
                    />
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-border/60 bg-card p-5">
                <h3 className="font-heading mb-4 flex items-center gap-2 text-sm font-semibold">
                  <Wallet className="h-4 w-4 text-accent-gold" />
                  Budget &amp; valeur
                </h3>
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label>Budget (texte)</Label>
                    <Input
                      value={form.budget}
                      onChange={(e) => set("budget", e.target.value)}
                      placeholder="ex: 60M - 80M FCFA"
                      data-testid="edit-budget"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Montant prévisionnel (FCFA)</Label>
                    <Input
                      type="number" min={0} step={100000}
                      value={form.montant_prevu}
                      onChange={(e) => set("montant_prevu", e.target.value)}
                      placeholder="ex: 75000000"
                      data-testid="edit-montant-prevu"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Probabilité (%)</Label>
                    <Input
                      type="number" min={0} max={100}
                      value={form.probabilite}
                      onChange={(e) => set("probabilite", e.target.value)}
                      placeholder="auto selon statut"
                      data-testid="edit-probabilite"
                    />
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-border/60 bg-card p-5 lg:col-span-3">
                <h3 className="font-heading mb-4 flex items-center gap-2 text-sm font-semibold">
                  <CalendarClock className="h-4 w-4 text-muted-foreground" />
                  Suivi commercial
                </h3>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  <div className="space-y-1.5">
                    <Label>Commercial assigné</Label>
                    <Input
                      value={form.commercial}
                      onChange={(e) => set("commercial", e.target.value)}
                      placeholder="ex: Moussa Ndiaye"
                      data-testid="edit-commercial"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Source</Label>
                    <Input value={form.source} onChange={(e) => set("source", e.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Date de réception</Label>
                    <Input
                      type="date"
                      value={form.date_reception}
                      onChange={(e) => set("date_reception", e.target.value)}
                      data-testid="edit-date-reception"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>1er contact WhatsApp</Label>
                    <Input
                      type="date"
                      value={form.premier_contact_whatsapp}
                      onChange={(e) => set("premier_contact_whatsapp", e.target.value)}
                      data-testid="edit-whatsapp"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Prochaine action</Label>
                    <Input type="date" value={form.prochaine_action} onChange={(e) => set("prochaine_action", e.target.value)} />
                  </div>
                  <div className="space-y-1.5 md:col-span-2 lg:col-span-3">
                    <Label>Notes</Label>
                    <Textarea
                      value={form.notes}
                      onChange={(e) => set("notes", e.target.value)}
                      rows={4}
                      data-testid="edit-notes"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <Button
                onClick={save}
                disabled={saving}
                data-testid="page-save-btn"
                className="gap-2"
              >
                <Save className="h-4 w-4" />
                {saving ? "Enregistrement..." : "Enregistrer"}
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="whatsapp" className="pt-5 animate-fade-in">
            <WhatsAppConversation
              lead={lead}
              onMessageSent={async () => {
                try {
                  const fresh = await leadsApi.get(lead.id);
                  setLead(fresh);
                } catch {}
              }}
            />
          </TabsContent>

          <TabsContent value="rappels" className="pt-5 animate-fade-in">
            <RappelsSection leadId={lead.id} />
          </TabsContent>

          <TabsContent value="procedure" className="pt-5 animate-fade-in">
            <ProcedureChecklist
              procedure={lead.procedure || {}}
              onToggle={handleProcedureToggle}
            />
          </TabsContent>

          <TabsContent value="programme" className="pt-5 animate-fade-in">
            <Programme20j
              programme={lead.programme || []}
              onToggle={handleProgrammeToggle}
            />
          </TabsContent>

          <TabsContent value="historique" className="pt-5 animate-fade-in">
            <div className="rounded-xl border border-border/60 bg-card p-5 sm:p-6">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <div className="text-label text-muted-foreground">Timeline</div>
                  <h2 className="font-heading mt-0.5 text-lg font-semibold">
                    {history.length} interaction{history.length > 1 ? "s" : ""}
                  </h2>
                </div>
              </div>
              {history.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">Aucune action enregistrée.</p>
              ) : (
                <ol className="relative space-y-4 border-l border-border/60 pl-5">
                  {history.map((h) => {
                    const Icon = ICONS[h.type] || FileText;
                    return (
                      <li key={h.id} className="group relative">
                        <span className="absolute -left-[27px] top-0.5 flex h-5 w-5 items-center justify-center rounded-full border border-border bg-background shadow-soft">
                          <Icon className="h-3 w-3 text-muted-foreground" />
                        </span>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="text-sm">{h.description}</div>
                            <div className="text-xs text-muted-foreground">{formatDateTime(h.date)}</div>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={async () => {
                              if (!window.confirm("Supprimer cette entrée d'historique ?")) return;
                              try {
                                const updated = await leadsApi.deleteHistory(lead.id, h.id);
                                setLead(updated);
                                toast.success("Entrée supprimée");
                              } catch (e) {
                                toast.error("Erreur lors de la suppression");
                              }
                            }}
                            data-testid={`history-delete-${h.id}`}
                            title="Supprimer cette entrée"
                            className="h-6 w-6 flex-none p-0 text-muted-foreground opacity-0 transition-opacity hover:text-red-500 group-hover:opacity-100"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Floating Action Bar (mobile) */}
      <div className="fixed inset-x-0 bottom-[60px] z-30 sm:hidden">
        <div className="mx-3 mb-2 flex items-center justify-around rounded-2xl border border-border/60 bg-background/95 p-1.5 shadow-floating backdrop-blur safe-bottom">
          <Button size="sm" variant="ghost" onClick={handleCall} className="flex-1 flex-col gap-0.5 px-2 py-2 h-auto" data-testid="fab-call">
            <Phone className="h-4 w-4 text-accent-blue" />
            <span className="text-[10px]">Appeler</span>
          </Button>
          <Button size="sm" variant="ghost" onClick={handleWhatsApp} className="flex-1 flex-col gap-0.5 px-2 py-2 h-auto" data-testid="fab-wa">
            <MessageCircle className="h-4 w-4 text-emerald-600" />
            <span className="text-[10px]">WhatsApp</span>
          </Button>
          <Button size="sm" variant="ghost" onClick={handleSMS} className="flex-1 flex-col gap-0.5 px-2 py-2 h-auto" data-testid="fab-sms">
            <MessageSquare className="h-4 w-4" />
            <span className="text-[10px]">SMS</span>
          </Button>
          <Button size="sm" variant="ghost" onClick={handleEmail} className="flex-1 flex-col gap-0.5 px-2 py-2 h-auto" data-testid="fab-email">
            <Mail className="h-4 w-4" />
            <span className="text-[10px]">Email</span>
          </Button>
          <Button size="sm" variant="ghost" onClick={markHot} className="flex-1 flex-col gap-0.5 px-2 py-2 h-auto" data-testid="fab-hot">
            <Flame className="h-4 w-4 text-red-500" />
            <span className="text-[10px]">HOT</span>
          </Button>
        </div>
      </div>

      {/* "Marquer perdu" confirmation modal. Forces a reason pick + adds
          a free-text note to the lead's history so post-mortem reporting
          can break down losses by cause. */}
      <Dialog open={lostDialogOpen} onOpenChange={setLostDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="lost-dialog">
          <DialogHeader>
            <DialogTitle>Marquer comme perdu</DialogTitle>
            <DialogDescription>
              Tu vas passer ce lead en statut « Perdu ». Précise la raison —
              c'est utile pour comprendre ce qui se joue dans ton portefeuille.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Raison de la perte</Label>
              <Select value={lostReason} onValueChange={setLostReason}>
                <SelectTrigger data-testid="lost-reason-trigger">
                  <SelectValue placeholder="Choisir une raison…" />
                </SelectTrigger>
                <SelectContent>
                  {LOST_REASONS.map((r) => (
                    <SelectItem key={r.value} value={r.value} data-testid={`lost-reason-${r.value}`}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Précisions (optionnel)</Label>
              <Textarea
                value={lostNote}
                onChange={(e) => setLostNote(e.target.value)}
                rows={3}
                placeholder="Ex : préférait un T3 plus grand, est parti voir la concurrence…"
                data-testid="lost-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setLostDialogOpen(false)}>
              Annuler
            </Button>
            <Button
              onClick={confirmLost}
              disabled={!lostReason}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="lost-confirm-btn"
            >
              Confirmer la perte
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
